import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Shield, Save, Check, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { rolesApi } from '../../api/endpoints/users';
import { queryClient } from '../../providers/QueryProvider';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageSpinner } from '../../components/ui/Spinner';

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  dashboard: 'View dashboard overview and stats',
  clients: 'View and manage tunnel clients',
  monitoring: 'Access monitoring dashboard',
  logs: 'View access and event logs',
  settings: 'Configure application settings',
  download: 'Download client installer',
  users: 'Manage user accounts',
  integrations: 'Manage API integrations',
  partners: 'Manage partner access',
  proxy: 'Use proxy forwarding',
  analytics: 'View analytics and alert rules',
  vendors: 'Manage vendor portal',
};

const roleBadgeVariant: Record<string, 'purple' | 'blue' | 'gray'> = {
  admin: 'purple',
  operator: 'blue',
  viewer: 'gray',
};

export function RolesPage() {
  const [permissionsMap, setPermissionsMap] = useState<Record<string, string[]>>({});
  const [savedRoles, setSavedRoles] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleName, permissions }: { roleName: string; permissions: string[] }) =>
      rolesApi.updatePermissions(roleName, permissions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setSaveError('');
      setSavedRoles(prev => new Set([...prev, variables.roleName]));
      setTimeout(() => {
        setSavedRoles(prev => {
          const next = new Set(prev);
          next.delete(variables.roleName);
          return next;
        });
      }, 2000);
    },
    onError: (err: Error, variables) => {
      setSaveError(`Failed to save ${variables.roleName}: ${err.message}`);
    },
  });

  const roles = data?.roles || [];
  // Filter out permissions that don't have a corresponding page
  const HIDDEN_PERMISSIONS = ['download'];
  const permissionKeys = (data?.permissionKeys || []).filter(pk => !HIDDEN_PERMISSIONS.includes(pk.key));

  // Initialize permissions map from fetched data
  useEffect(() => {
    if (roles.length > 0) {
      const map: Record<string, string[]> = {};
      roles.forEach(role => {
        map[role.name] = [...role.permissions];
      });
      setPermissionsMap(map);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePermission = useCallback((roleName: string, permKey: string) => {
    if (roleName === 'admin') return;
    setPermissionsMap(prev => {
      const current = prev[roleName] || [];
      const has = current.includes(permKey);
      return {
        ...prev,
        [roleName]: has ? current.filter(k => k !== permKey) : [...current, permKey],
      };
    });
  }, []);

  const handleSave = (roleName: string) => {
    const permissions = permissionsMap[roleName] || [];
    updateMutation.mutate({ roleName, permissions });
  };

  const handleSaveAll = () => {
    roles
      .filter(r => r.name !== 'admin' && hasChanged(r.name))
      .forEach(role => {
        handleSave(role.name);
      });
  };

  const hasChanged = (roleName: string): boolean => {
    const original = roles.find(r => r.name === roleName);
    if (!original) return false;
    const current = permissionsMap[roleName] || [];
    if (current.length !== original.permissions.length) return true;
    return current.some(p => !original.permissions.includes(p)) ||
           original.permissions.some(p => !current.includes(p));
  };

  const anyChanged = roles.some(r => r.name !== 'admin' && hasChanged(r.name));

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Role Permissions</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
            Configure which permissions each role has access to
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/settings"
            className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-xs flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
            <Shield size={16} />
            <span className="text-xs font-medium">{roles.length} roles, {permissionKeys.length} permissions</span>
          </div>
        </div>
      </div>

      {/* Error display */}
      {saveError && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-2 rounded-md text-xs">
          {saveError}
        </div>
      )}

      {/* Permission Matrix */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-900/50 z-10">
                  Permission
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-900/50 z-10 hidden md:table-cell">
                  Description
                </th>
                {roles.map(role => (
                  <th
                    key={role.name}
                    className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Badge variant={roleBadgeVariant[role.name] || 'gray'}>
                        {role.displayName || role.name}
                      </Badge>
                      {role.name === 'admin' && (
                        <span className="text-[10px] text-gray-400 normal-case font-normal">(all access)</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {permissionKeys.map(pk => (
                <tr key={pk.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap text-xs sticky left-0 bg-white dark:bg-[#111] z-10">
                    <div>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{pk.label}</span>
                      <p className="text-[10px] text-gray-400 font-mono">{pk.key}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">
                    {PERMISSION_DESCRIPTIONS[pk.key] || ''}
                  </td>
                  {roles.map(role => {
                    const isAdmin = role.name === 'admin';
                    const perms = permissionsMap[role.name] || [];
                    const checked = isAdmin || perms.includes(pk.key);
                    return (
                      <td key={role.name} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isAdmin}
                          onChange={() => togglePermission(role.name, pk.key)}
                          className={`w-4 h-4 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 ${
                            isAdmin
                              ? 'text-gray-400 cursor-not-allowed opacity-50'
                              : 'text-purple-600 cursor-pointer'
                          }`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {permissionKeys.length === 0 && (
                <tr>
                  <td
                    colSpan={roles.length + 2}
                    className="text-center py-8 text-sm text-gray-500 dark:text-gray-400"
                  >
                    No permissions defined
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Save buttons */}
        {roles.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Admin role always has full access and cannot be modified.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {roles.filter(r => r.name !== 'admin').map(role => (
                <button
                  key={role.name}
                  onClick={() => handleSave(role.name)}
                  disabled={updateMutation.isPending || !hasChanged(role.name)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                    savedRoles.has(role.name)
                      ? 'bg-green-600 text-white'
                      : hasChanged(role.name)
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {savedRoles.has(role.name) ? <Check size={12} /> : <Save size={12} />}
                  {savedRoles.has(role.name)
                    ? `${role.displayName || role.name} Saved`
                    : `Save ${role.displayName || role.name}`}
                </button>
              ))}
              {anyChanged && (
                <button
                  onClick={handleSaveAll}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  <Save size={12} />
                  Save All Changes
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Role Descriptions */}
      <div className="mt-5">
        <Card title="Role Descriptions">
          <div className="p-4 space-y-2">
            {roles.map(role => {
              const permCount = role.name === 'admin' ? permissionKeys.length : (permissionsMap[role.name] || []).length;
              return (
                <div
                  key={role.name}
                  className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <Badge variant={roleBadgeVariant[role.name] || 'gray'}>
                    {role.displayName || role.name}
                  </Badge>
                  <div className="flex-1">
                    {role.name === 'admin' && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Full system access. All permissions are always granted.
                      </p>
                    )}
                    {role.name === 'operator' && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Day-to-day operations. Can manage clients, view monitoring and logs.
                      </p>
                    )}
                    {role.name === 'viewer' && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Read-only access. Can view dashboard and client status.
                      </p>
                    )}
                    {!['admin', 'operator', 'viewer'].includes(role.name) && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Custom role with configured permissions.
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {permCount} permission{permCount !== 1 ? 's' : ''} granted
                      {role.isSystem && (
                        <span className="ml-1 text-purple-400">(system role)</span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
